import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { RequestDataAccessService } from 'data-access';
import { NotificationService } from 'src/app/service/notification-service';
import { NavigationService } from 'src/app/service/navigation.service';
import { RequestRoot } from 'types';
import { ActivityCommand, IBadge, NavigationBackHandler } from 'src/app/model/activity';
import { AppContext, Command, CommandContext, CommandPage, CommandSideEffects, CommandWrapper } from 'controls';
import { SelectableItem } from 'src/app/model/selectable-item';
import { DeleteCommand } from 'src/app/command/delete-command';
import { Haptics } from '@capacitor/haptics';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'requests',
    templateUrl: './requests.component.html',
    styleUrls: ['./requests.component.scss'],
})
export class RequestsComponent extends CommandPage implements OnInit, OnDestroy {
    private badge: IBadge = { content: 0, hidden: true };
    public deleteCommand: Command<RequestRoot> | null = null;
    public notificationsCommand: ActivityCommand | null = null;
    public items: SelectableItem<RequestRoot>[] = [];
    private dead$: Subject<void> = new Subject();

    constructor(
        private router: Router,
        private appContext: AppContext,
        private cmdContext: CommandContext,
        private navigationService: NavigationService,
        private notificationService: NotificationService,
        private requestService: RequestDataAccessService
    ) {
        super();
    }

    ngOnInit(): void {
        this.navigationService.activity.reset();
        this.navigationService.activity.onBack = (dh) => this.onNavigationBackRequested(dh);
        this.deleteCommand = new DeleteCommand('command.request.delete', this.notificationService);
        this.getCommandCompleted$()
            .pipe(takeUntil(this.dead$))
            .subscribe((r: CommandSideEffects) => {
                this.onCommandCompleted(r);
            });
        this.initializeNotificationsCommand();
        this.reloadRequestList();
        this.updateActivitySettings();
    }

    ngOnDestroy(): void {
        this.dead$.next();
        this.dead$.complete();
    }

    onNavigationBackRequested(defaultBackHandler: NavigationBackHandler): void {
        if (this.isAnyItemSelected()) {
            this.items.forEach((i) => (i.selected = false));
            this.updateActivitySettings();
        } else {
            defaultBackHandler();
        }
    }

    onRequestListReceived(list: RequestRoot[]): void {
        this.items = list.map((r) => new SelectableItem(r));
        this.updateActivitySettings();
    }

    onRequestClick(id?: string): void {
        if (this.isAnyItemSelected() && id) {
            this.toggleItemSelection(id);
            return;
        }

        const item = this.items.find((r) => r.data.id === id);
        if (item) {
            this.router.navigate([item.data.status === 'DATA_ENTERED' ? '/request-editor' : '/request-preview'], { queryParams: { id } });
        }
    }

    onRequestLongPress(id?: string): void {
        Haptics.vibrate({ duration: 50 });

        if (id) {
            this.toggleItemSelection(id);
        } else {
            console.error('No request ID');
        }
    }

    onCommandCompleted(effects: CommandSideEffects): void {
        if (effects.databaseModified) {
            this.reloadRequestList();
            this.updateNotificationCounter();
        }
    }

    reloadRequestList(): void {
        this.requestService
            .find({})
            .pipe(takeUntil(this.dead$))
            .subscribe({
                next: (list) => {
                    this.onRequestListReceived(list);
                },
                error: (error) => {
                    console.error(error);
                },
            });
    }

    updateNotificationCounter(): void {
        this.notificationService
            .countUnread()
            .pipe(takeUntil(this.dead$))
            .subscribe((count) => {
                this.badge.content = count;
                this.badge.hidden = count <= 0;
            });
    }

    isAnyItemSelected(): boolean {
        return this.items && this.items.some((i) => i.selected);
    }

    toggleItemSelection(id: string): void {
        const item = this.items.find((i) => i.data.id === id);

        if (item) {
            item.selected = !item.selected;
            this.updateActivitySettings();
        }
    }

    updateActivitySettings(): void {
        if (this.isAnyItemSelected()) {
            this.navigationService.activity.toolbar.setTitle(`Выбрано: ${this.items.filter((item) => item.selected).length}`);
            this.navigationService.activity.toolbar.setOptions([
                {
                    title: 'Удалить',
                    icon: 'delete',
                    action: () => {
                        if (this.deleteCommand) {
                            this.executeCommand(this.deleteCommand);
                        }
                    },
                    enabled: () => true,
                },
            ]);
        } else {
            this.navigationService.activity.toolbar.setTitle('Заявления');
            if (this.notificationsCommand) {
                this.navigationService.activity.toolbar.setOptions([this.notificationsCommand]);
            }
        }
    }

    private initializeNotificationsCommand(): void {
        this.notificationsCommand = new ActivityCommand();
        this.notificationsCommand.icon = 'notifications';
        this.notificationsCommand.badge = this.badge;
        this.notificationsCommand.enabled = () => true;
        this.notificationsCommand.action = () => this.router.navigate(['/notifications']);
        this.updateNotificationCounter();
    }

    private executeCommand(command: Command<RequestRoot>): void {
        let selection = this.items.filter((i) => i.selected);
        let wrapper = new CommandWrapper(this.appContext, this.cmdContext, this, command);
        wrapper.execute(selection.map((i) => i.data));
    }
}
